import { useEffect, useRef, useState } from 'react'
import {
  BarChart3,
  Bike,
  ClipboardList,
  LineChart,
  Shield,
  TrendingDown,
  Warehouse,
  ArrowUpRight,
  Activity,
  Target,
} from 'lucide-react'

const publicUrl = process.env.PUBLIC_URL || ''

const landingCss = `
  .ra-lp {
    --lp-vw: #001E50;
    --lp-vw-light: #1a3a7e;
    --lp-surface: #1a1d21;
    --lp-surface-2: #16181c;
    --lp-border: rgba(226,232,240,0.10);
    --lp-border-hover: rgba(226,232,240,0.22);
    --lp-text: #f8fafc;
    --lp-muted: rgba(203,213,225,0.74);
    --lp-sub: rgba(226,232,240,0.68);
    --lp-tertiary: rgba(226,232,240,0.46);
    --lp-success: #86efac;
    --lp-danger: #fca5a5;
    --lp-warning: #fcd34d;
    min-height: 100vh;
    background: #121416;
    color: var(--lp-text);
    font-family: 'Inter', 'Roboto', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    position: relative;
    overflow-x: hidden;
  }

  /* ============ BACKGROUND LAYERS ============ */
  .ra-lp__bg {
    pointer-events: none;
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse 100% 60% at 50% -10%, rgba(0, 30, 80, 0.45), transparent 55%),
      radial-gradient(ellipse 80% 40% at 85% 30%, rgba(0, 30, 80, 0.18), transparent 60%),
      linear-gradient(180deg, #14161a 0%, #121416 45%, #0f1113 100%);
    z-index: 0;
  }
  .ra-lp__grid {
    pointer-events: none;
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(rgba(226,232,240,0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(226,232,240,0.025) 1px, transparent 1px);
    background-size: 56px 56px;
    mask-image: radial-gradient(ellipse 75% 60% at 50% 20%, black, transparent);
    z-index: 0;
  }
  .ra-lp__scan {
    pointer-events: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(100, 140, 220, 0.4) 40%,
      rgba(100, 140, 220, 0.6) 50%,
      rgba(100, 140, 220, 0.4) 60%,
      transparent 100%
    );
    z-index: 1;
    animation: raScan 8s ease-in-out infinite;
  }
  @keyframes raScan {
    0%, 100% { transform: translateY(0); opacity: 0; }
    5% { opacity: 1; }
    50% { transform: translateY(100vh); opacity: 0.8; }
    55% { opacity: 0; }
  }

  .ra-lp__inner {
    position: relative;
    z-index: 2;
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 1.5rem 4rem;
  }

  /* ============ NAV (full-width) ============ */
  .ra-lp__nav {
    position: sticky;
    top: 0;
    z-index: 50;
    background: rgba(18, 20, 22, 0.82);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--lp-border);
    margin-bottom: 4rem;
    transition: border-color 0.3s, background 0.3s;
  }
  .ra-lp__nav--scrolled {
    border-bottom-color: rgba(226,232,240,0.18);
    background: rgba(18, 20, 22, 0.92);
  }
  .ra-lp__nav-inner {
    max-width: 1100px;
    margin: 0 auto;
    padding: 1.1rem 1.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  .ra-lp__brand {
    display: inline-flex;
    align-items: center;
    gap: 0.65rem;
    text-decoration: none;
    color: inherit;
  }
  .ra-lp__brand img {
    width: 28px;
    height: 28px;
    object-fit: contain;
    display: block;
    transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .ra-lp__brand:hover img { transform: rotate(-8deg) scale(1.05); }
  .ra-lp__wordmark {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--lp-text);
  }
  .ra-lp__tag {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--lp-tertiary);
    border: 1px solid var(--lp-border);
    padding: 3px 8px;
    border-radius: 4px;
    margin-left: 4px;
  }
  .ra-lp__tag--beta {
    color: rgba(180, 210, 255, 0.95);
    border-color: rgba(80, 120, 200, 0.45);
    background: rgba(0, 30, 80, 0.35);
    position: relative;
    overflow: hidden;
  }
  .ra-lp__tag--beta::before {
    content: '';
    position: absolute;
    top: 0;
    left: -120%;
    width: 40%;
    height: 100%;
    background: linear-gradient(90deg,
      transparent,
      rgba(180, 210, 255, 0.25),
      transparent
    );
    animation: raBetaShine 4s ease-in-out infinite;
  }
  @keyframes raBetaShine {
    0%, 100% { left: -120%; }
    50% { left: 220%; }
  }
  .ra-lp__nav-actions { display: flex; align-items: center; gap: 10px; }

  /* ============ BUTTONS ============ */
  .ra-lp__btn {
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border-radius: 6px;
    padding: 10px 18px;
    border: 1px solid transparent;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .ra-lp__btn--ghost {
    background: transparent;
    border-color: var(--lp-border);
    color: var(--lp-sub);
  }
  .ra-lp__btn--ghost:hover {
    border-color: var(--lp-border-hover);
    color: var(--lp-text);
    background: rgba(255,255,255,0.03);
  }
  .ra-lp__btn--primary {
    background: rgba(0, 30, 80, 0.7);
    border-color: rgba(80, 120, 200, 0.45);
    color: #e2e8f0;
    box-shadow: 0 0 0 0 rgba(0, 30, 80, 0);
  }
  .ra-lp__btn--primary:hover {
    background: rgba(0, 50, 120, 0.85);
    border-color: rgba(130, 170, 240, 0.6);
    color: #fff;
    box-shadow: 0 0 24px rgba(0, 50, 120, 0.35);
    transform: translateY(-1px);
  }
  .ra-lp__btn--primary .ra-lp__arrow {
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .ra-lp__btn--primary:hover .ra-lp__arrow {
    transform: translate(2px, -2px);
  }

  /* ============ HERO ============ */
  .ra-lp__hero {
    padding: 1rem 0 2.5rem;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  .ra-lp__eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--lp-tertiary);
    margin-bottom: 1.1rem;
    padding: 6px 12px;
    border: 1px solid var(--lp-border);
    border-radius: 100px;
    background: rgba(26, 29, 33, 0.6);
  }
  .ra-lp__eyebrow-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--lp-success);
    box-shadow: 0 0 8px rgba(134, 239, 172, 0.6);
    animation: raPulse 2s ease-in-out infinite;
  }
  @keyframes raPulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(1.15); }
  }
  .ra-lp__hero-title {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 400;
    letter-spacing: -0.035em;
    line-height: 1.12;
    color: var(--lp-text);
    max-width: 14.5ch;
    margin-bottom: 1.4rem;
  }
  .ra-lp__hero-title em {
    font-style: normal;
    color: rgba(180, 210, 255, 0.98);
    font-weight: 500;
    position: relative;
  }
  .ra-lp__hero-title em::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -2px;
    height: 1px;
    background: linear-gradient(90deg,
      transparent,
      rgba(180, 210, 255, 0.4),
      transparent
    );
  }
  .ra-lp__lead {
    font-size: 16px;
    line-height: 1.65;
    color: var(--lp-sub);
    max-width: 34em;
    margin-bottom: 2rem;
  }
  .ra-lp__hero-cta {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    margin-bottom: 3.5rem;
  }

  /* ============ TICKER — live market strip ============ */
  .ra-lp__ticker {
    border: 1px solid var(--lp-border);
    border-radius: 6px;
    background: var(--lp-surface-2);
    overflow: hidden;
    margin-bottom: 5rem;
    position: relative;
  }
  .ra-lp__ticker-label {
    position: absolute;
    top: 50%;
    left: 16px;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--lp-tertiary);
    background: var(--lp-surface-2);
    padding: 4px 10px 4px 4px;
    z-index: 2;
  }
  .ra-lp__ticker-label::after {
    content: '';
    position: absolute;
    right: -20px;
    top: 0;
    bottom: 0;
    width: 20px;
    background: linear-gradient(90deg, var(--lp-surface-2), transparent);
  }
  .ra-lp__ticker-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #fca5a5;
    animation: raPulse 1.4s ease-in-out infinite;
  }
  .ra-lp__ticker-track {
    display: flex;
    gap: 3rem;
    padding: 14px 0 14px 140px;
    animation: raTicker 45s linear infinite;
    white-space: nowrap;
  }
  @keyframes raTicker {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  .ra-lp__ticker-item {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: var(--lp-sub);
  }
  .ra-lp__ticker-model { color: var(--lp-muted); font-weight: 500; }
  .ra-lp__ticker-val { color: var(--lp-text); font-weight: 600; }
  .ra-lp__ticker-delta { font-weight: 600; font-size: 11px; }
  .ra-lp__ticker-delta--pos { color: var(--lp-success); }
  .ra-lp__ticker-delta--neg { color: var(--lp-danger); }

  /* ============ REVEAL ANIMATION ============ */
  .ra-reveal {
    opacity: 0;
    transform: translateY(24px);
    transition: opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1),
                transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .ra-reveal.ra-reveal--in {
    opacity: 1;
    transform: translateY(0);
  }
  .ra-reveal--delay-1 { transition-delay: 0.08s; }
  .ra-reveal--delay-2 { transition-delay: 0.16s; }
  .ra-reveal--delay-3 { transition-delay: 0.24s; }
  .ra-reveal--delay-4 { transition-delay: 0.32s; }
  @media (prefers-reduced-motion: reduce) {
    .ra-reveal, .ra-reveal--in {
      opacity: 1;
      transform: none;
      transition: none;
    }
    .ra-lp__scan, .ra-lp__ticker-track, .ra-lp__eyebrow-dot, .ra-lp__ticker-dot {
      animation: none;
    }
    .ra-lp__tag--beta::before { animation: none; }
  }

  /* ============ SECTIONS ============ */
  .ra-lp__section { margin-bottom: 5rem; }
  .ra-lp__section-head {
    margin-bottom: 2rem;
    max-width: 36em;
  }
  .ra-lp__section-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--lp-tertiary);
    margin-bottom: 0.9rem;
  }
  .ra-lp__section-kicker-line {
    width: 24px;
    height: 1px;
    background: var(--lp-border-hover);
  }
  .ra-lp__section-h2 {
    font-size: clamp(1.35rem, 2.6vw, 1.75rem);
    font-weight: 500;
    letter-spacing: -0.028em;
    line-height: 1.25;
    color: var(--lp-text);
    margin-bottom: 0.65rem;
  }
  .ra-lp__section-sub {
    font-size: 14px;
    line-height: 1.6;
    color: var(--lp-sub);
  }

  /* ============ CARDS ============ */
  .ra-lp__cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1px;
    background: var(--lp-border);
    border: 1px solid var(--lp-border);
    border-radius: 6px;
    overflow: hidden;
  }
  @media (max-width: 720px) { .ra-lp__cards { grid-template-columns: 1fr; } }
  .ra-lp__card {
    background: var(--lp-surface);
    padding: 1.65rem 1.65rem 1.75rem;
    position: relative;
    transition: background 0.25s;
  }
  .ra-lp__card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at var(--mx, 50%) var(--my, 50%),
      rgba(100, 140, 220, 0.08),
      transparent 40%);
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
  }
  .ra-lp__card:hover { background: #1d2024; }
  .ra-lp__card:hover::before { opacity: 1; }
  .ra-lp__card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }
  .ra-lp__card-icon {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 30, 80, 0.25);
    border: 1px solid rgba(80, 120, 200, 0.3);
    color: rgba(180, 210, 255, 0.92);
  }
  .ra-lp__card-icon svg { width: 17px; height: 17px; stroke-width: 1.75; }
  .ra-lp__card-num {
    font-size: 10px;
    font-weight: 600;
    color: var(--lp-tertiary);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.08em;
  }
  .ra-lp__card-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--lp-text);
    margin-bottom: 8px;
    letter-spacing: -0.01em;
  }
  .ra-lp__card-desc {
    font-size: 13px;
    line-height: 1.6;
    color: var(--lp-sub);
  }

  /* ============ METRICS BAND ============ */
  .ra-lp__metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1px;
    background: var(--lp-border);
    border: 1px solid var(--lp-border);
    border-radius: 6px;
    overflow: hidden;
  }
  @media (max-width: 720px) { .ra-lp__metrics { grid-template-columns: repeat(2, 1fr); } }
  .ra-lp__metric {
    background: var(--lp-surface);
    padding: 1.4rem 1.25rem;
  }
  .ra-lp__metric-k {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--lp-tertiary);
    margin-bottom: 10px;
  }
  .ra-lp__metric-v {
    font-size: 28px;
    font-weight: 300;
    font-variant-numeric: tabular-nums;
    color: var(--lp-text);
    letter-spacing: -0.025em;
    line-height: 1;
    margin-bottom: 6px;
  }
  .ra-lp__metric-s {
    font-size: 11px;
    color: var(--lp-tertiary);
    line-height: 1.4;
  }

  /* ============ SPLIT / FEATURE ROW ============ */
  .ra-lp__feature {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
    align-items: center;
    padding: 2rem 0;
  }
  @media (max-width: 860px) {
    .ra-lp__feature { grid-template-columns: 1fr; gap: 1.5rem; }
    .ra-lp__feature--reverse .ra-lp__feature-visual { order: -1; }
  }
  .ra-lp__feature + .ra-lp__feature {
    border-top: 1px solid var(--lp-border);
  }

  /* ---- Mock : stock row ---- */
  .ra-lp__mock {
    border: 1px solid var(--lp-border);
    border-radius: 6px;
    padding: 1.1rem 1.2rem 1.3rem;
    background: var(--lp-surface-2);
    font-size: 12px;
    position: relative;
    overflow: hidden;
  }
  .ra-lp__mock::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(90deg,
      transparent,
      rgba(180, 210, 255, 0.04),
      transparent
    );
    animation: raShimmer 6s ease-in-out infinite;
  }
  @keyframes raShimmer {
    0%, 100% { left: -100%; }
    50% { left: 100%; }
  }
  .ra-lp__mock-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--lp-border);
    margin-bottom: 14px;
  }
  .ra-lp__mock-title {
    font-weight: 600;
    color: var(--lp-text);
    font-size: 13px;
    letter-spacing: -0.01em;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .ra-lp__mock-status {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--lp-tertiary);
    padding: 3px 7px;
    border: 1px solid var(--lp-border);
    border-radius: 3px;
  }
  .ra-lp__mock-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 9px 0;
    border-bottom: 1px solid rgba(226,232,240,0.06);
  }
  .ra-lp__mock-row:last-child { border-bottom: none; }
  .ra-lp__mock-k {
    color: var(--lp-tertiary);
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 500;
  }
  .ra-lp__mock-v {
    font-weight: 600;
    color: var(--lp-text);
    font-variant-numeric: tabular-nums;
    font-size: 13px;
  }
  .ra-lp__mock-bar {
    height: 3px;
    background: rgba(226,232,240,0.08);
    border-radius: 2px;
    overflow: hidden;
    margin-top: 10px;
    position: relative;
  }
  .ra-lp__mock-bar-fill {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    background: linear-gradient(90deg,
      rgba(252, 165, 165, 0.4),
      rgba(252, 165, 165, 0.8)
    );
    width: 0;
    animation: raFillBar 2.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    animation-delay: 0.4s;
  }
  @keyframes raFillBar {
    to { width: 72%; }
  }
  .ra-lp__mock-foot {
    margin-top: 14px;
    font-size: 10px;
    color: var(--lp-tertiary);
    line-height: 1.45;
  }

  /* ---- Mock : chart ---- */
  .ra-lp__chart {
    border: 1px solid var(--lp-border);
    border-radius: 6px;
    padding: 1.25rem;
    background: var(--lp-surface-2);
    position: relative;
  }
  .ra-lp__chart-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 1rem;
  }
  .ra-lp__chart-title {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--lp-tertiary);
  }
  .ra-lp__chart-val {
    font-size: 13px;
    font-weight: 600;
    color: var(--lp-text);
    font-variant-numeric: tabular-nums;
  }
  .ra-lp__chart-svg { width: 100%; height: auto; display: block; }
  .ra-lp__chart-line {
    fill: none;
    stroke: rgba(180, 210, 255, 0.85);
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 600;
    stroke-dashoffset: 600;
    animation: raDraw 2.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    animation-delay: 0.3s;
  }
  .ra-lp__chart-area {
    fill: url(#ra-chart-grad);
    opacity: 0;
    animation: raFadeIn 1s ease-out forwards;
    animation-delay: 2.2s;
  }
  @keyframes raDraw { to { stroke-dashoffset: 0; } }
  @keyframes raFadeIn { to { opacity: 1; } }
  .ra-lp__chart-dot {
    fill: #fff;
    stroke: rgba(180, 210, 255, 1);
    stroke-width: 1.5;
    opacity: 0;
    animation: raFadeIn 0.4s ease-out forwards;
    animation-delay: 2.6s;
  }
  .ra-lp__chart-grid {
    stroke: rgba(226,232,240,0.05);
    stroke-width: 1;
  }
  .ra-lp__chart-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    font-size: 10px;
    color: var(--lp-tertiary);
    font-variant-numeric: tabular-nums;
  }

  /* ============ PROCESS ============ */
  .ra-lp__process {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
  }
  @media (max-width: 720px) { .ra-lp__process { grid-template-columns: 1fr; } }
  .ra-lp__step {
    border: 1px solid var(--lp-border);
    border-radius: 6px;
    padding: 1.4rem 1.35rem 1.5rem;
    background: var(--lp-surface);
    position: relative;
    transition: border-color 0.25s, transform 0.25s;
  }
  .ra-lp__step:hover {
    border-color: var(--lp-border-hover);
    transform: translateY(-2px);
  }
  .ra-lp__step-num {
    font-size: 10px;
    font-weight: 600;
    color: var(--lp-tertiary);
    letter-spacing: 0.14em;
    margin-bottom: 1.2rem;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .ra-lp__step-num::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--lp-border);
  }
  .ra-lp__step-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--lp-text);
    margin-bottom: 8px;
    letter-spacing: -0.01em;
  }
  .ra-lp__step-desc {
    font-size: 13px;
    line-height: 1.6;
    color: var(--lp-sub);
  }

  /* ============ CTA FINAL ============ */
  .ra-lp__cta-block {
    text-align: center;
    padding: 3rem 1.5rem;
    border: 1px solid var(--lp-border);
    border-radius: 6px;
    background: linear-gradient(180deg, var(--lp-surface), var(--lp-surface-2));
    position: relative;
    overflow: hidden;
  }
  .ra-lp__cta-block::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60%;
    height: 1px;
    background: linear-gradient(90deg,
      transparent,
      rgba(180, 210, 255, 0.35),
      transparent
    );
  }
  .ra-lp__cta-head {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    justify-content: center;
  }
  .ra-lp__cta-block h2 {
    font-size: 1.35rem;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: var(--lp-text);
  }
  .ra-lp__cta-block p {
    font-size: 14px;
    color: var(--lp-sub);
    line-height: 1.6;
    max-width: 34em;
    margin: 0 auto 1.75rem;
  }

  /* ============ FOOTER ============ */
  .ra-lp__footer {
    padding-top: 2.5rem;
    margin-top: 3rem;
    border-top: 1px solid var(--lp-border);
    font-size: 11px;
    color: var(--lp-tertiary);
    line-height: 1.5;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1rem;
  }
  .ra-lp__footer-left {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .ra-lp__footer-nav {
    display: flex;
    gap: 1.5rem;
  }
  .ra-lp__footer-nav a {
    color: var(--lp-tertiary);
    text-decoration: none;
    transition: color 0.2s;
  }
  .ra-lp__footer-nav a:hover { color: var(--lp-muted); }

  @media (max-width: 900px) {
    .ra-lp__inner {
      padding: 0 1rem 2.25rem;
    }
    .ra-lp__nav {
      margin-bottom: 2rem;
    }
    .ra-lp__nav-inner {
      padding: 0.8rem 1rem;
    }
    .ra-lp__hero {
      padding: 0.4rem 0 1.5rem;
    }
    .ra-lp__hero-title {
      max-width: 100%;
      margin-bottom: 0.9rem;
    }
    .ra-lp__lead {
      font-size: 14px;
      line-height: 1.58;
      margin-bottom: 1.2rem;
    }
    .ra-lp__hero-cta {
      margin-bottom: 1.6rem;
    }
    .ra-lp__ticker {
      margin-bottom: 2.2rem;
    }
    .ra-lp__section {
      margin-bottom: 2.5rem;
    }
    .ra-lp__section-head {
      margin-bottom: 1.15rem;
    }
    .ra-lp__feature {
      padding: 1.25rem 0;
      gap: 1.15rem;
    }
    .ra-lp__cta-block {
      padding: 2rem 1rem;
    }
  }

  @media (max-width: 640px) {
    .ra-lp__nav-inner {
      flex-wrap: wrap;
      justify-content: center;
      gap: 0.75rem;
    }
    .ra-lp__brand {
      width: 100%;
      justify-content: center;
    }
    .ra-lp__nav-actions {
      width: 100%;
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: stretch;
      gap: 8px;
    }
    .ra-lp__nav-actions .ra-lp__btn {
      width: calc(50% - 4px);
      max-width: calc(50% - 4px);
    }
    .ra-lp__btn {
      flex: 1 1 0;
      min-width: 0;
      min-height: 42px;
      justify-content: center;
      white-space: nowrap;
      padding: 9px 8px;
      font-size: 11px;
    }
    .ra-lp__eyebrow {
      width: 100%;
      justify-content: center;
      text-align: center;
      margin-bottom: 0.7rem;
    }
    .ra-lp__hero-title {
      font-size: clamp(1.55rem, 7vw, 2.1rem);
      line-height: 1.16;
      text-align: center;
    }
    .ra-lp__lead {
      text-align: center;
      max-width: none;
    }
    .ra-lp__hero-cta {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
      width: 100%;
    }
    .ra-lp__ticker {
      margin-bottom: 1.7rem;
    }
    .ra-lp__ticker-label {
      position: static;
      transform: none;
      width: fit-content;
      margin: 8px 8px 0;
      background: transparent;
      padding-right: 0;
    }
    .ra-lp__ticker-label::after {
      display: none;
    }
    .ra-lp__ticker-track {
      padding: 8px 0 10px 10px;
      gap: 1.35rem;
      animation-duration: 55s;
    }
    .ra-lp__ticker-item {
      font-size: 11px;
      gap: 8px;
    }
    .ra-lp__metric {
      padding: 1.1rem 0.9rem;
    }
    .ra-lp__metric-v {
      font-size: 24px;
    }
    .ra-lp__card {
      padding: 1.15rem 1rem 1.2rem;
    }
    .ra-lp__card-title {
      font-size: 14px;
    }
    .ra-lp__card-desc {
      font-size: 12px;
      line-height: 1.55;
    }
    .ra-lp__section-sub {
      font-size: 13px;
      line-height: 1.52;
    }
    .ra-lp__mock,
    .ra-lp__chart {
      padding: 0.9rem;
    }
    .ra-lp__process {
      gap: 0.8rem;
    }
    .ra-lp__step {
      padding: 1rem 0.95rem 1.05rem;
    }
    .ra-lp__step-title {
      font-size: 14px;
    }
    .ra-lp__step-desc {
      font-size: 12px;
      line-height: 1.55;
    }
    .ra-lp__footer {
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 0.7rem;
      padding-top: 1.6rem;
      margin-top: 2rem;
    }
    .ra-lp__footer-nav {
      gap: 0.85rem;
      flex-wrap: wrap;
      justify-content: center;
    }
  }

  @media (max-width: 420px) {
    .ra-lp__grid,
    .ra-lp__scan {
      display: none;
    }
    .ra-lp__inner {
      padding: 0 0.75rem 1.8rem;
    }
    .ra-lp__nav-inner {
      padding: 0.75rem;
    }
    .ra-lp__nav-actions {
      gap: 6px;
    }
    .ra-lp__nav-actions .ra-lp__btn {
      width: calc(50% - 3px);
      max-width: calc(50% - 3px);
    }
    .ra-lp__wordmark {
      font-size: 14px;
    }
    .ra-lp__tag {
      font-size: 8px;
      padding: 2px 6px;
    }
    .ra-lp__eyebrow {
      font-size: 9px;
      padding: 5px 9px;
      letter-spacing: 0.12em;
    }
    .ra-lp__btn {
      min-height: 40px;
      padding: 8px 6px;
      font-size: 10.5px;
      letter-spacing: 0;
    }
    .ra-lp__lead {
      font-size: 13px;
    }
    .ra-lp__section {
      margin-bottom: 2.2rem;
    }
    .ra-lp__section-h2 {
      font-size: 1.14rem;
    }
    .ra-lp__ticker-track {
      animation: none;
      overflow-x: auto;
      white-space: nowrap;
      padding: 6px 8px 9px;
      scrollbar-width: thin;
    }
    .ra-lp__ticker-track::-webkit-scrollbar {
      height: 4px;
    }
    .ra-lp__ticker-track::-webkit-scrollbar-thumb {
      background: rgba(226,232,240,0.2);
      border-radius: 8px;
    }
    .ra-lp__metric-k {
      font-size: 9px;
      margin-bottom: 6px;
    }
    .ra-lp__metric-v {
      font-size: 22px;
    }
    .ra-lp__metric-s {
      font-size: 10px;
    }
  }
`

// ============================================================
// DATA
// ============================================================

const pillars = [
  {
    Icon: BarChart3,
    title: 'Cotation & référence marché',
    desc: 'Fourchette de prix, vente probable selon l\'historique des transactions, dispersion et niveau de confiance — pour arbitrer prix d\'affichage et reprises sans approximation.',
  },
  {
    Icon: Warehouse,
    title: 'Mon stock & concurrence',
    desc: 'Vue concession : rotation, alertes sur motos dormantes, concurrence par modèle et fiches détaillées avec positionnement prix vs marché en temps réel.',
  },
  {
    Icon: TrendingDown,
    title: 'Marché vivant & opportunités',
    desc: 'Baisses de prix récentes, liquidité par modèle et annonces sous-cotées : une veille orientée décision, pas un simple listing.',
  },
  {
    Icon: LineChart,
    title: 'Leasing & propositions',
    desc: 'Scénarios de financement calés sur la valeur résiduelle prédite, historique par véhicule et propositions exportables pour vos équipes commerciales.',
  },
]

const metrics = [
  { k: 'Annonces actives', v: '31 900', s: 'Motos suivies · Suisse' },
  { k: 'Cotations recalculées', v: '4 100', s: 'Fenêtre 2 h' },
  { k: 'Précision cotation', v: '±4.2 %', s: 'Médian sur 90 j' },
  { k: 'Mise à jour', v: 'Quotidienne', s: 'Scrape + enrichissement' },
]

const steps = [
  {
    num: '01',
    title: 'Branchement stock',
    desc: 'Votre stock est rapproché des annonces marché par signature modèle, année et kilométrage — sans double saisie.',
  },
  {
    num: '02',
    title: 'Cotation continue',
    desc: 'Chaque véhicule est coté quotidiennement. L\'écart vs marché et le score de liquidité se mettent à jour seuls.',
  },
  {
    num: '03',
    title: 'Actions priorisées',
    desc: 'Les opportunités d\'achat, les motos à repriser et les risques sont remontés au bon moment, pas en vrac.',
  },
]

const tickerData = [
  { model: 'Yamaha MT-09 2023', val: '9 800 CHF', delta: '+2.1%', dir: 'pos' },
  { model: 'Honda CB-125 2023', val: '3 993 CHF', delta: '−7.0%', dir: 'neg' },
  { model: 'BMW R 1250 GS', val: '18 400 CHF', delta: '+0.8%', dir: 'pos' },
  { model: 'Ducati Panigale V4', val: '22 100 CHF', delta: '−3.2%', dir: 'neg' },
  { model: 'Yamaha XSR 900', val: '8 990 CHF', delta: '−4.5%', dir: 'neg' },
  { model: 'KTM 390 Duke', val: '4 650 CHF', delta: '+1.4%', dir: 'pos' },
  { model: 'Kawasaki Z900', val: '7 820 CHF', delta: '−1.8%', dir: 'neg' },
  { model: 'Triumph Street Triple', val: '8 350 CHF', delta: '+0.3%', dir: 'pos' },
]

// ============================================================
// HOOKS
// ============================================================

function useReveal() {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true)
            obs.unobserve(e.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return [ref, visible]
}

function Reveal({ children, delay = 0, as: As = 'div', className = '', ...rest }) {
  const [ref, visible] = useReveal()
  const delayCls = delay ? ` ra-reveal--delay-${delay}` : ''
  return (
    <As
      ref={ref}
      className={`ra-reveal${visible ? ' ra-reveal--in' : ''}${delayCls} ${className}`}
      {...rest}
    >
      {children}
    </As>
  )
}

function useScrolled(threshold = 24) {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [threshold])
  return scrolled
}

// ============================================================
// SUB-COMPONENTS
// ============================================================

function PriceChart() {
  // Points pour la courbe d'évolution des prix marché (illustratif)
  const points = [
    [0, 58], [40, 54], [80, 52], [120, 56], [160, 48],
    [200, 44], [240, 46], [280, 38], [320, 34], [360, 32],
  ]
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ')
  const areaD = `${pathD} L360,90 L0,90 Z`
  const last = points[points.length - 1]
  return (
    <div className="ra-lp__chart">
      <div className="ra-lp__chart-head">
        <span className="ra-lp__chart-title">Médiane marché · 90 j</span>
        <span className="ra-lp__chart-val">−8.4 %</span>
      </div>
      <svg viewBox="0 0 360 90" className="ra-lp__chart-svg" preserveAspectRatio="none">
        <defs>
          <linearGradient id="ra-chart-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(180, 210, 255, 0.18)" />
            <stop offset="100%" stopColor="rgba(180, 210, 255, 0)" />
          </linearGradient>
        </defs>
        <line x1="0" y1="22" x2="360" y2="22" className="ra-lp__chart-grid" strokeDasharray="2 4" />
        <line x1="0" y1="60" x2="360" y2="60" className="ra-lp__chart-grid" strokeDasharray="2 4" />
        <path d={areaD} className="ra-lp__chart-area" />
        <path d={pathD} className="ra-lp__chart-line" />
        <circle cx={last[0]} cy={last[1]} r="3.5" className="ra-lp__chart-dot" />
      </svg>
      <div className="ra-lp__chart-labels">
        <span>J−90</span>
        <span>J−60</span>
        <span>J−30</span>
        <span>Aujourd'hui</span>
      </div>
    </div>
  )
}

function StockMock() {
  return (
    <div className="ra-lp__mock">
      <div className="ra-lp__mock-head">
        <span className="ra-lp__mock-title">
          <Bike size={14} strokeWidth={1.75} color="rgba(180,210,255,0.9)" />
          Fiche stock · YZF-R1
        </span>
        <span className="ra-lp__mock-status">Surcotée</span>
      </div>
      <div className="ra-lp__mock-row">
        <span className="ra-lp__mock-k">Prix affiché</span>
        <span className="ra-lp__mock-v">14 200 CHF</span>
      </div>
      <div className="ra-lp__mock-row">
        <span className="ra-lp__mock-k">Vs ventes réelles</span>
        <span className="ra-lp__mock-v" style={{ color: 'var(--lp-danger)' }}>+8.2 %</span>
      </div>
      <div className="ra-lp__mock-row">
        <span className="ra-lp__mock-k">Liquidité modèle</span>
        <span className="ra-lp__mock-v">7.2 / 10</span>
      </div>
      <div className="ra-lp__mock-row">
        <span className="ra-lp__mock-k">Jours en ligne</span>
        <span className="ra-lp__mock-v">62 j</span>
      </div>
      <div className="ra-lp__mock-bar">
        <div className="ra-lp__mock-bar-fill" />
      </div>
      <div className="ra-lp__mock-foot">
        Illustration · positionnement vs médiane des ventes 12 derniers mois.
      </div>
    </div>
  )
}

// ============================================================
// MAIN
// ============================================================

/**
 * Page d'accueil publique — ton concessionnaire, aligné sur le design system du dashboard.
 */
export function LandingPage({ onConnect, onRequestAccess }) {
  const scrolled = useScrolled()

  // Parallaxe léger du fond sur scroll
  useEffect(() => {
    const bg = document.querySelector('.ra-lp__bg')
    if (!bg) return
    const onScroll = () => {
      const y = window.scrollY * 0.15
      bg.style.transform = `translate3d(0, ${y}px, 0)`
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Hover radial sur les cards
  const handleCardMove = (e) => {
    const card = e.currentTarget
    const r = card.getBoundingClientRect()
    card.style.setProperty('--mx', `${e.clientX - r.left}px`)
    card.style.setProperty('--my', `${e.clientY - r.top}px`)
  }

  return (
    <div className="ra-lp">
      <style>{landingCss}</style>
      <div className="ra-lp__bg" aria-hidden />
      <div className="ra-lp__grid" aria-hidden />
      <div className="ra-lp__scan" aria-hidden />

      <header className={`ra-lp__nav${scrolled ? ' ra-lp__nav--scrolled' : ''}`}>
        <div className="ra-lp__nav-inner">
          <a href="/" className="ra-lp__brand" onClick={(e) => e.preventDefault()}>
            <img src={`${publicUrl}/logo-icon.png`} alt="RadarAuto" width={28} height={28} />
            <span className="ra-lp__wordmark">RadarAuto</span>
            <span className="ra-lp__tag ra-lp__tag--beta">Beta</span>
          </a>
          <div className="ra-lp__nav-actions">
            <button type="button" className="ra-lp__btn ra-lp__btn--ghost" onClick={onRequestAccess}>
              Demander un accès
            </button>
            <button type="button" className="ra-lp__btn ra-lp__btn--primary" onClick={onConnect}>
              Connexion
              <ArrowUpRight size={13} strokeWidth={2} className="ra-lp__arrow" />
            </button>
          </div>
        </div>
      </header>

      <div className="ra-lp__inner">

        {/* ============ HERO ============ */}
        <section className="ra-lp__hero">
          <Reveal>
            <span className="ra-lp__eyebrow">
              <span className="ra-lp__eyebrow-dot" />
              Occasion véhicule · Concessionnaires · Suisse
            </span>
          </Reveal>
          <Reveal delay={1}>
            <h1 className="ra-lp__hero-title">
              Vendez mieux votre <em>stock moto</em>.
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="ra-lp__lead">
              Cotation, veille marché et priorités d'action dans un seul dashboard pour votre concession.
            </p>
          </Reveal>
          <Reveal delay={3}>
            <div className="ra-lp__hero-cta">
              <button type="button" className="ra-lp__btn ra-lp__btn--primary" onClick={onConnect}>
                Accéder à l'espace concession
                <ArrowUpRight size={13} strokeWidth={2} className="ra-lp__arrow" />
              </button>
              <button type="button" className="ra-lp__btn ra-lp__btn--ghost" onClick={onRequestAccess}>
                Créer un compte concession
              </button>
            </div>
          </Reveal>
        </section>

        {/* ============ TICKER ============ */}
        <Reveal>
          <div className="ra-lp__ticker" aria-label="Tendances marché en temps réel">
            <div className="ra-lp__ticker-label">
              <span className="ra-lp__ticker-dot" />
              Live
            </div>
            <div className="ra-lp__ticker-track">
              {[...tickerData, ...tickerData].map((t, i) => (
                <div className="ra-lp__ticker-item" key={i}>
                  <span className="ra-lp__ticker-model">{t.model}</span>
                  <span className="ra-lp__ticker-val">{t.val}</span>
                  <span className={`ra-lp__ticker-delta ra-lp__ticker-delta--${t.dir}`}>
                    {t.delta}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* ============ METRICS ============ */}
        <section className="ra-lp__section">
          <Reveal>
            <div className="ra-lp__section-head">
              <div className="ra-lp__section-kicker">
                <span className="ra-lp__section-kicker-line" />
                Couverture & précision
              </div>
              <h2 className="ra-lp__section-h2">Une base de référence construite sur la donnée réelle du marché suisse</h2>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <div className="ra-lp__metrics">
              {metrics.map((m) => (
                <div key={m.k} className="ra-lp__metric">
                  <div className="ra-lp__metric-k">{m.k}</div>
                  <div className="ra-lp__metric-v">{m.v}</div>
                  <div className="ra-lp__metric-s">{m.s}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        {/* ============ PILLARS ============ */}
        <section className="ra-lp__section">
          <Reveal>
            <div className="ra-lp__section-head">
              <div className="ra-lp__section-kicker">
                <span className="ra-lp__section-kicker-line" />
                Modules
              </div>
              <h2 className="ra-lp__section-h2">Ce que vos équipes utilisent au quotidien</h2>
              <p className="ra-lp__section-sub">
                Quatre briques complémentaires, conçues pour réduire le temps passé à chercher et augmenter la qualité
                des décisions en reprise, en affichage et en closing.
              </p>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <div className="ra-lp__cards">
              {pillars.map(({ Icon, title, desc }, i) => (
                <article
                  key={title}
                  className="ra-lp__card"
                  onMouseMove={handleCardMove}
                >
                  <div className="ra-lp__card-head">
                    <div className="ra-lp__card-icon" aria-hidden>
                      <Icon />
                    </div>
                    <span className="ra-lp__card-num">0{i + 1}</span>
                  </div>
                  <h3 className="ra-lp__card-title">{title}</h3>
                  <p className="ra-lp__card-desc">{desc}</p>
                </article>
              ))}
            </div>
          </Reveal>
        </section>

        {/* ============ FEATURE 1 : Cotation ============ */}
        <section className="ra-lp__section">
          <div className="ra-lp__feature">
            <Reveal>
              <div>
                <div className="ra-lp__section-kicker">
                  <Target size={12} strokeWidth={2} />
                  Cotation
                </div>
                <h2 className="ra-lp__section-h2">Référence de cotation différenciée, pas un score magique</h2>
                <p className="ra-lp__section-sub">
                  Quand les ventes réelles sont suffisantes, la référence bascule automatiquement sur les prix de
                  transaction confirmés plutôt que sur les prix affichés. L'écart reporté devient actionnable
                  — il reflète ce qu'un acheteur a effectivement payé.
                </p>
              </div>
            </Reveal>
            <Reveal delay={1} className="ra-lp__feature-visual">
              <StockMock />
            </Reveal>
          </div>

          <div className="ra-lp__feature ra-lp__feature--reverse">
            <Reveal className="ra-lp__feature-visual">
              <PriceChart />
            </Reveal>
            <Reveal delay={1}>
              <div>
                <div className="ra-lp__section-kicker">
                  <Activity size={12} strokeWidth={2} />
                  Marché vivant
                </div>
                <h2 className="ra-lp__section-h2">La tendance, pas juste l'instantané</h2>
                <p className="ra-lp__section-sub">
                  Suivez l'évolution de la médiane marché par modèle sur 30, 60 et 90 jours. Les accélérations de
                  baisse sont isolées pour repérer à temps les modèles qui se dévalorisent plus vite que prévu.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ============ PROCESS ============ */}
        <section className="ra-lp__section">
          <Reveal>
            <div className="ra-lp__section-head">
              <div className="ra-lp__section-kicker">
                <span className="ra-lp__section-kicker-line" />
                Mise en route
              </div>
              <h2 className="ra-lp__section-h2">Trois étapes, pas un projet IT</h2>
            </div>
          </Reveal>
          <div className="ra-lp__process">
            {steps.map((s, i) => (
              <Reveal key={s.num} delay={i + 1}>
                <div className="ra-lp__step">
                  <div className="ra-lp__step-num">{s.num}</div>
                  <div className="ra-lp__step-title">{s.title}</div>
                  <div className="ra-lp__step-desc">{s.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ============ CTA ============ */}
        <Reveal>
          <section className="ra-lp__cta-block">
            <div className="ra-lp__cta-head">
              <Shield size={18} strokeWidth={1.75} color="rgba(148,163,184,0.85)" />
              <h2>Accès réservé aux professionnels</h2>
            </div>
            <p>
              L'inscription est validée par concession afin de garder des données et des usages alignés sur le métier
              occasion. Une fois connecté, vous retrouvez le dashboard, la cotation, le marché, votre stock et le
              leasing dans un même environnement.
            </p>
            <button type="button" className="ra-lp__btn ra-lp__btn--primary" onClick={onRequestAccess}>
              Demander un accès concession
              <ArrowUpRight size={13} strokeWidth={2} className="ra-lp__arrow" />
            </button>
          </section>
        </Reveal>

        <footer className="ra-lp__footer">
          <span className="ra-lp__footer-left">
            <ClipboardList size={12} strokeWidth={2} aria-hidden />
            © {new Date().getFullYear()} RadarAuto — intelligence marché deux-roues — Suisse
          </span>
          <nav className="ra-lp__footer-nav">
            <a href="/" onClick={(e) => e.preventDefault()}>Mentions légales</a>
            <a href="/" onClick={(e) => e.preventDefault()}>Confidentialité</a>
            <a href="/" onClick={(e) => e.preventDefault()}>Contact</a>
          </nav>
        </footer>
      </div>
    </div>
  )
}
